import React, { useState, useCallback } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CloudUpload, FolderZip } from '@mui/icons-material';
import { MAX_IMAGE_FILE_SIZE, MAX_ZIP_FILE_SIZE } from '../utils/uploadConstants';

const ACCEPTED_EXTENSIONS = '.jpg,.jpeg,.png,.tiff,.tif,.webp,.gif,.heic,.heif,.zip';

const ACCEPTED_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/tif',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
  'application/zip',
  'application/x-zip-compressed',
]);

interface BulkDropZoneProps {
  onFilesSelected: (files: File[]) => void;
  isUploading?: boolean;
  /**
   * When true, suppresses drop/click handling and dims the zone.
   * Used to gate uploads on a precondition outside the drop zone's
   * concern — e.g. "select a catalog before uploading".
   */
  disabled?: boolean;
  /** Optional tooltip/label shown when ``disabled`` is true. */
  disabledMessage?: string;
  'data-testid'?: string;
}

export const BulkDropZone: React.FC<BulkDropZoneProps> = ({
  onFilesSelected,
  isUploading = false,
  disabled = false,
  disabledMessage,
  'data-testid': testId,
}) => {
  const theme = useTheme();
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inert = isUploading || disabled;

  const validateFiles = useCallback((files: FileList | File[]): File[] => {
    const valid: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      const isZip = file.type === 'application/zip' || file.type === 'application/x-zip-compressed' || file.name.endsWith('.zip');
      const isImage = ACCEPTED_TYPES.has(file.type) && !isZip;

      if (!isImage && !isZip) {
        errors.push(`${file.name}: unsupported format`);
        return;
      }

      const maxSize = isZip ? MAX_ZIP_FILE_SIZE : MAX_IMAGE_FILE_SIZE;
      if (file.size > maxSize) {
        const maxMB = Math.round(maxSize / (1024 * 1024));
        errors.push(`${file.name}: exceeds ${maxMB} MB limit`);
        return;
      }

      valid.push(file);
    });

    if (errors.length > 0) {
      setValidationError(errors.join('; '));
    } else {
      setValidationError(null);
    }

    return valid;
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (inert) return;

      const files = validateFiles(e.dataTransfer.files);
      if (files.length > 0) {
        onFilesSelected(files);
      }
    },
    [inert, onFilesSelected, validateFiles],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || e.target.files.length === 0) return;
      const files = validateFiles(e.target.files);
      if (files.length > 0) {
        onFilesSelected(files);
      }
      e.target.value = '';
    },
    [onFilesSelected, validateFiles],
  );

  return (
    <Box data-testid={testId} data-disabled={disabled ? 'true' : 'false'}>
      <Paper
        onDragEnter={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        sx={{
          p: 6,
          textAlign: 'center',
          cursor: inert ? 'default' : 'pointer',
          border: '2px dashed',
          borderColor: isDragOver
            ? theme.palette.primary.main
            : theme.palette.divider,
          borderRadius: 2,
          bgcolor: isDragOver
            ? theme.palette.action.hover
            : 'transparent',
          transition: 'all 0.2s ease',
          opacity: inert ? 0.6 : 1,
          '&:hover': inert
            ? {}
            : {
                borderColor: theme.palette.primary.light,
                bgcolor: theme.palette.action.hover,
              },
        }}
        onClick={() => {
          if (!inert) {
            document.getElementById('bulk-file-input')?.click();
          }
        }}
        title={disabled ? disabledMessage : undefined}
      >
        <input
          id="bulk-file-input"
          type="file"
          multiple
          accept={ACCEPTED_EXTENSIONS}
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
          <CloudUpload sx={{ fontSize: 48, color: theme.palette.text.secondary }} />
          <FolderZip sx={{ fontSize: 48, color: theme.palette.text.secondary }} />
        </Box>

        <Typography variant="h6" gutterBottom>
          Drop images or ZIP files here
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Supports JPG, PNG, TIFF, WebP, GIF, HEIC, and ZIP archives (up to 500 MB)
        </Typography>
      </Paper>

      {validationError && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {validationError}
        </Typography>
      )}
    </Box>
  );
};
